// apps-kb-cursor.js - Virtual cursor rendering (REFACTORED)
// Now works in logical rank/file space, only converting to pixels for rendering

import state from './apps-state.js';
import { keyboardState } from './apps-kb-state.js';
import * as helpers from './apps-kb-helpers.js';
import * as navLogic from './apps-kb-navigation-logic.js';

/**
 * Blur all focusable elements in nav and header
 */
function blurNavAndHeader() {
    // Create or get the hidden focus target
    let hiddenFocus = document.getElementById('hidden-focus-target');
    if (!hiddenFocus) {
        hiddenFocus = document.createElement('div');
        hiddenFocus.id = 'hidden-focus-target';
        hiddenFocus.tabIndex = -1; // Not in tab order, but can be programmatically focused
        hiddenFocus.style.cssText = `
            position: fixed;
            top: -9999px;
            left: -9999px;
            width: 1px;
            height: 1px;
            opacity: 0;
            pointer-events: none;
        `;
        hiddenFocus.setAttribute('aria-hidden', 'true');
        document.body.appendChild(hiddenFocus);
    }
    
    // Focus the hidden element - this will blur everything else
    hiddenFocus.focus();
}

/**
 * Main cursor display function - works entirely in rank/file space
 * Only converts to pixels at the final rendering step
 */
export function showVirtualCursor(rank, file) {
    hideVirtualCursor();
    
    // Blur all nav/header elements
    blurNavAndHeader();
    
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const currentScreen = helpers.getCurrentScreen();
    
    // Update logical state
    keyboardState.virtualCursor.active = true;
    keyboardState.virtualCursor.rank = rank;
    keyboardState.virtualCursor.file = file;
    
    // Handle special zones
    if (rank === -1) {
        // Top modal zone
        keyboardState.virtualCursor.isInModalZone = true;
        keyboardState.virtualCursor.modalZonePosition = 'top';
        return; // Modal zones are handled elsewhere
    }
    
    if (rank === -0.5 && isFolder) {
        // Folder title
        const folderTitle = document.getElementById('folderTitle');
        if (folderTitle) {
            folderTitle.focus();
            return;
        }
    }
    
    const maxRank = navLogic.getMaxRank(layout, isFolder, keyboardState.moveMode.active);
    if (rank > maxRank) {
        // Bottom modal zone
        keyboardState.virtualCursor.isInModalZone = true;
        keyboardState.virtualCursor.modalZonePosition = 'bottom';
        return; // Modal zones are handled elsewhere
    }
    
    if (file === -1 || file >= navLogic.getMaxFile(layout) + 1) {
        // Scroll zones - render specially in move mode
        if (keyboardState.moveMode.active) {
            renderCursorAtScrollZone(rank, file, layout, isFolder);
        }
        return;
    }
    
    // Check for empty screen with delete button
    if (!keyboardState.moveMode.active && helpers.isCurrentScreenEmptyWithDeleteButton()) {
        renderCursorOnDeleteButton();
        return;
    }
    
    // Handle insert positions (fractional files)
    if (navLogic.isInsertPosition(file)) {
        renderCursorAtInsertPosition(rank, file, layout, isFolder, currentScreen);
        return;
    }
    
    // Regular icon slot position
    renderCursorAtIconSlot(rank, file, layout, isFolder, currentScreen);
}

/**
 * Render cursor at scroll zone (file -1 or MAX+1)
 * Position ghost 2/3 off screen, arrow centered on scroll zone indicator
 */
function renderCursorAtScrollZone(rank, file, layout, isFolder) {
    const maxFile = navLogic.getMaxFile(layout);
    const isLeftZone = file === -1;
    const isRightZone = file >= maxFile + 1;
    
    // Get the scroll zone element
    const scrollZoneId = isFolder ? 
        (isLeftZone ? 'folderScrollZoneLeft' : 'folderScrollZoneRight') :
        (isLeftZone ? 'scrollZoneLeft' : 'scrollZoneRight');
    
    const scrollZone = document.getElementById(scrollZoneId);
    if (!scrollZone) return;
    
    const scrollRect = scrollZone.getBoundingClientRect();
    
    // Center arrow on scroll zone horizontally, keep vertical position based on rank
    const arrowX = scrollRect.left + scrollRect.width / 2;
    
    // Get vertical position based on rank
    const pixelPos = getIconSlotPixels(rank, isLeftZone ? 0 : maxFile, layout, isFolder, helpers.getCurrentScreen());
    const arrowY = pixelPos ? pixelPos.centerY : scrollRect.top + scrollRect.height / 2;
    
    createArrow(arrowX, arrowY);
    
    // Position ghost icon 2/3 off screen
    if (state.dragGhost) {
        const viewport = isFolder ?
            document.getElementById('folderContainer') :
            document.getElementById('appspaceViewport');
        
        if (viewport) {
            const viewportRect = viewport.getBoundingClientRect();
            const iconSize = parseInt(
                getComputedStyle(document.documentElement).getPropertyValue('--icon-size')
            ) || 80;
            
            let ghostX, ghostY;
            
            if (isLeftZone) {
                // Position 2/3 off left edge
                ghostX = viewportRect.left - (iconSize * 2/3);
            } else {
                // Position 2/3 off right edge
                ghostX = viewportRect.right - (iconSize * 1/3);
            }
            
            // Center vertically based on rank
            ghostY = arrowY - (iconSize / 2);
            
            state.dragGhost.style.left = ghostX + 'px';
            state.dragGhost.style.top = ghostY + 'px';
        }
    }
}

/**
 * Render cursor at a regular icon slot (integer rank and file)
 */
function renderCursorAtIconSlot(rank, file, layout, isFolder, currentScreen) {
    const pixelPos = rankFileToPixelPosition(rank, file, layout, isFolder, currentScreen);
    
    if (!pixelPos) {
        console.error('Could not calculate pixel position for rank/file:', { rank, file });
        return;
    }
    
    // Create cursor box
    const cursor = document.createElement('div');
    cursor.id = 'virtualCursor';
    cursor.style.cssText = `
        position: fixed;
        top: ${pixelPos.top}px;
        left: ${pixelPos.left}px;
        width: ${pixelPos.width}px;
        height: ${pixelPos.height}px;
        border: 3px solid rgba(74, 144, 226, 0.8);
        border-radius: 8px;
        pointer-events: none;
        z-index: 9999;
        box-shadow: 0 0 20px rgba(74, 144, 226, 0.5);
        animation: pulse 1.5s ease-in-out infinite;
    `;
    
    document.body.appendChild(cursor);
    
    // Create arrow pointer at center
    createArrow(pixelPos.centerX, pixelPos.centerY);
    
    // Position ghost icon at slot center
    if (state.dragGhost) {
        const iconSize = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--icon-size')
        ) || 80;
        
        const ghostX = pixelPos.centerX - (iconSize / 2);
        const ghostY = pixelPos.centerY - (iconSize / 2);
        
        state.dragGhost.style.left = ghostX + 'px';
        state.dragGhost.style.top = ghostY + 'px';
    }
    
    // Add pulse animation if not already present
    ensurePulseAnimation();
}

/**
 * Render cursor at an insert position (fractional file)
 */
function renderCursorAtInsertPosition(rank, file, layout, isFolder, currentScreen) {
    // Determine insert type
    const maxFile = navLogic.getMaxFile(layout);
    
    if (file === -0.5) {
        keyboardState.virtualCursor.isInInsertZone = true;
        keyboardState.virtualCursor.insertZoneType = 'before';
    } else if (file === maxFile + 0.5) {
        keyboardState.virtualCursor.isInInsertZone = true;
        keyboardState.virtualCursor.insertZoneType = 'after';
    } else {
        keyboardState.virtualCursor.isInInsertZone = true;
        keyboardState.virtualCursor.insertZoneType = 'between';
    }
    
    const pixelPos = rankFileToPixelPosition(rank, file, layout, isFolder, currentScreen);
    
    if (!pixelPos) {
        console.error('Could not calculate insert position for rank/file:', { rank, file });
        return;
    }
    
    // Create visual insert indicator bar
    const indicator = document.createElement('div');
    indicator.id = 'virtualCursor';
    indicator.style.cssText = `
        position: fixed;
        top: ${pixelPos.top}px;
        left: ${pixelPos.left}px;
        width: ${pixelPos.width}px;
        height: ${pixelPos.height}px;
        background: rgba(74, 144, 226, 0.3);
        border: 2px solid rgba(74, 144, 226, 0.8);
        border-radius: 4px;
        pointer-events: none;
        z-index: 9999;
        box-shadow: 0 0 15px rgba(74, 144, 226, 0.6);
    `;
    
    document.body.appendChild(indicator);
    
    // Show arrow at insert position
    createArrow(pixelPos.centerX, pixelPos.centerY);
    
    // Position ghost icon at insert position
    if (state.dragGhost) {
        const iconSize = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--icon-size')
        ) || 80;
        
        const ghostX = pixelPos.centerX - (iconSize / 2);
        const ghostY = pixelPos.centerY - (iconSize / 2);
        
        state.dragGhost.style.left = ghostX + 'px';
        state.dragGhost.style.top = ghostY + 'px';
    }
}

/**
 * Render cursor on delete button (empty screen)
 */
function renderCursorOnDeleteButton() {
    const deleteButton = helpers.getDeleteButtonElement();
    if (!deleteButton) return;
    
    const rect = deleteButton.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    
    keyboardState.virtualCursor.isOnDeleteButton = true;
    keyboardState.virtualCursor.rank = 0; // Arbitrary
    keyboardState.virtualCursor.file = 0;
    
    createArrow(centerX, centerY);
    
    // Position ghost icon at delete button
    if (state.dragGhost) {
        const iconSize = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--icon-size')
        ) || 80;
        
        const ghostX = centerX - (iconSize / 2);
        const ghostY = centerY - (iconSize / 2);
        
        state.dragGhost.style.left = ghostX + 'px';
        state.dragGhost.style.top = ghostY + 'px';
    }
}

/**
 * Convert logical rank/file to pixel position
 * Returns { left, top, width, height, centerX, centerY } or null
 */
function rankFileToPixelPosition(rank, file, layout, isFolder, currentScreen) {
    // Handle insert positions (fractional files)
    if (navLogic.isInsertPosition(file)) {
        return getInsertPositionPixels(rank, file, layout, isFolder, currentScreen);
    }
    
    // Regular icon slot
    return getIconSlotPixels(rank, file, layout, isFolder, currentScreen);
}

/**
 * Get pixel coordinates for an icon slot
 */
function getIconSlotPixels(rank, file, layout, isFolder, currentScreen) {
    if (!Number.isInteger(rank) || !Number.isInteger(file)) return null;
    if (rank < 0 || file < 0) return null;
    
    const slotInfo = helpers.rankFileToRegionPosition(rank, file, layout, isFolder);
    if (!slotInfo) return null;
    
    const regionsPerScreen = isFolder ? layout.leaves : layout.leaves * layout.bands;
    const screenStartRegion = currentScreen * regionsPerScreen;
    const actualRegion = screenStartRegion + slotInfo.region;
    
    // Get the container and screen element
    const container = isFolder ? 
        document.getElementById('folderScreens') : 
        document.getElementById('appspaceScreens');
    
    if (!container) return null;
    
    const visualScreenIndex = currentScreen;
    const screenElement = container.children[visualScreenIndex];
    if (!screenElement) return null;
    
    // Find the slot element
    const slotElement = screenElement.querySelector(
        `.icon-slot[data-region="${actualRegion}"][data-position="${slotInfo.position}"]`
    );
    
    if (!slotElement) {
        console.warn(`Slot not found for region=${actualRegion}, position=${slotInfo.position}`);
        return null;
    }
    
    const slotRect = slotElement.getBoundingClientRect();
    
    // Check if there's an icon for more precise centering
    const iconElement = slotElement.querySelector('.appicon');
    const centerRect = iconElement ? iconElement.getBoundingClientRect() : slotRect;
    
    return {
        left: slotRect.left,
        top: slotRect.top,
        width: slotRect.width,
        height: slotRect.height,
        centerX: centerRect.left + centerRect.width / 2,
        centerY: centerRect.top + centerRect.height / 2
    };
}

/**
 * Get pixel coordinates for an insert position (between/before/after icons)
 */
function getInsertPositionPixels(rank, file, layout, isFolder, currentScreen) {
    const maxFile = navLogic.getMaxFile(layout);
    
    // Get the actual gap width from CSS
    const gapH = parseInt(
        getComputedStyle(document.documentElement).getPropertyValue('--icon-gap-horizontal')
    ) || state.config.gapHorizontal || 16;
    
    if (file === -0.5) {
        // Before first icon (file 0) - center within the gap to the left
        const firstSlot = getIconSlotPixels(rank, 0, layout, isFolder, currentScreen);
        if (!firstSlot) return null;
        
        return {
            left: firstSlot.left - gapH,
            top: firstSlot.top,
            width: gapH,
            height: firstSlot.height,
            centerX: firstSlot.left - (gapH / 2),
            centerY: firstSlot.centerY
        };
    } else if (file === maxFile + 0.5) {
        // After last icon (file MAX) - center within the gap to the right
        const lastSlot = getIconSlotPixels(rank, maxFile, layout, isFolder, currentScreen);
        if (!lastSlot) return null;
        
        return {
            left: lastSlot.left + lastSlot.width,
            top: lastSlot.top,
            width: gapH,
            height: lastSlot.height,
            centerX: lastSlot.left + lastSlot.width + (gapH / 2),
            centerY: lastSlot.centerY
        };
    } else if (file > 0 && file < maxFile + 0.5) {
        // Between two icons
        const leftFile = Math.floor(file);
        const rightFile = Math.ceil(file);
        
        const leftSlot = getIconSlotPixels(rank, leftFile, layout, isFolder, currentScreen);
        const rightSlot = getIconSlotPixels(rank, rightFile, layout, isFolder, currentScreen);
        
        if (!leftSlot || !rightSlot) return null;
        
        // Check if this is a region boundary (between two leaves)
        // A region has 4 files (0-3), so boundaries occur at 3->4, 7->8, 11->12, etc.
        const isRegionBoundary = (leftFile % 4 === 3) && (rightFile % 4 === 0);
        
        const centerX = (leftSlot.left + leftSlot.width + rightSlot.left) / 2;
        
        // Calculate the actual gap width
        const actualGapWidth = rightSlot.left - (leftSlot.left + leftSlot.width);
        
        const indicatorWidth = actualGapWidth;
        
        return {
            left: centerX - (indicatorWidth / 2),
            top: leftSlot.top,
            width: indicatorWidth,
            height: leftSlot.height,
            centerX,
            centerY: leftSlot.centerY
        };
    }
    
    return null;
}

/**
 * Create arrow element at specified pixel position
 */
function createArrow(clientX, clientY) {
    const arrow = document.createElement('div');
    arrow.id = 'virtualCursorArrow';
    arrow.style.cssText = `
        position: fixed;
        left: ${clientX}px;
        top: ${clientY}px;
        transform: translate(0, 0);
        pointer-events: none;
        z-index: 10000;
        filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.4));
    `;
    
    arrow.innerHTML = `
        <svg width="40" height="40" viewBox="0 0 24 24" style="display: block;">
            <defs>
                <linearGradient id="arrowGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                    <stop offset="0%" style="stop-color:#4a90e2;stop-opacity:1" />
                    <stop offset="100%" style="stop-color:#667eea;stop-opacity:1" />
                </linearGradient>
            </defs>
            <path d="m 2.5374192,2.4105467 0.015608,16.6231323 3.9763444,-4.619607 3.5539124,7.469772 2.798959,-1.343159 -3.553913,-7.469768 5.633253,0.0082 z"
                  fill="url(#arrowGradient)" 
                  stroke="white" 
                  stroke-width="1.25"
                  stroke-linejoin="round"/>
        </svg>
    `;
    
    document.body.appendChild(arrow);
}

/**
 * Ensure pulse animation CSS exists
 */
function ensurePulseAnimation() {
    if (!document.getElementById('virtualCursorStyles')) {
        const style = document.createElement('style');
        style.id = 'virtualCursorStyles';
        style.textContent = `
            @keyframes pulse {
                0%, 100% { opacity: 1; }
                50% { opacity: 0.6; }
            }
        `;
        document.head.appendChild(style);
    }
}

/**
 * Legacy function for positioning cursor at specific pixel coordinates
 * This is now deprecated in favor of showVirtualCursor(rank, file)
 * Kept for backwards compatibility during refactoring
 */
export function showVirtualCursorAtPosition(clientX, clientY, rank, file) {
    hideVirtualCursor();
    
    keyboardState.virtualCursor.active = true;
    keyboardState.virtualCursor.rank = rank;
    keyboardState.virtualCursor.file = file;
    
    if (navLogic.isInsertPosition(file)) {
        keyboardState.virtualCursor.isInInsertZone = true;
        const maxFile = navLogic.getMaxFile(helpers.getLayout());
        
        if (file === -0.5) {
            keyboardState.virtualCursor.insertZoneType = 'before';
        } else if (file === maxFile + 0.5) {
            keyboardState.virtualCursor.insertZoneType = 'after';
        } else {
            keyboardState.virtualCursor.insertZoneType = 'between';
        }
    }
    
    createArrow(clientX, clientY);
}

/**
 * Hide the virtual cursor
 */
export function hideVirtualCursor() {
    const cursor = document.getElementById('virtualCursor');
    if (cursor) cursor.remove();
    
    const arrow = document.getElementById('virtualCursorArrow');
    if (arrow) arrow.remove();
    
    // Blur the hidden focus target if it exists
    const hiddenFocus = document.getElementById('hidden-focus-target');
    if (hiddenFocus && hiddenFocus === document.activeElement) {
        hiddenFocus.blur();
    }
    
    keyboardState.virtualCursor.active = false;
    keyboardState.virtualCursor.isInModalZone = false;
    keyboardState.virtualCursor.isInInsertZone = false;
    keyboardState.virtualCursor.insertZoneType = null;
    keyboardState.virtualCursor.modalZonePosition = null;
    keyboardState.virtualCursor.isOnDeleteButton = false;
    
    // Hide both context menus when cursor is hidden
    import('./apps-kb-context-menu.js').then(contextMenu => {
        if (contextMenu && typeof contextMenu.hideContextMenu === 'function') {
            contextMenu.hideContextMenu();
        }
        if (contextMenu && typeof contextMenu.hideGlobalContextMenu === 'function') {
            contextMenu.hideGlobalContextMenu();
        }
    });
}

/**
 * Hide insert indicator (legacy compatibility)
 */
export function hideInsertIndicator() {
    const indicator = document.getElementById('insertIndicator');
    if (indicator) indicator.remove();
    
    keyboardState.virtualCursor.isInInsertZone = false;
    keyboardState.virtualCursor.insertZoneType = null;
}

/**
 * Update ghost icon position during drag
 */
export function updateGhostPosition(clientX, clientY) {
    if (state.dragGhost) {
        const computedIconSize = parseInt(
            getComputedStyle(document.documentElement).getPropertyValue('--icon-size')
        ) || state.config.iconSize;
        state.dragGhost.style.left = (clientX - computedIconSize / 2) + 'px';
        state.dragGhost.style.top = (clientY - computedIconSize / 2) + 'px';
    }
}

/**
 * Convert pixel coordinates to rank/file (for mouse/touch integration)
 */
export function pixelToRankFile(clientX, clientY) {
    const layout = helpers.getLayout();
    const isFolder = !!state.openFolder;
    const currentScreen = helpers.getCurrentScreen();
    
    const element = document.elementFromPoint(clientX, clientY);
    if (!element) return null;
    
    const slot = element.closest('.icon-slot');
    if (!slot) return null;
    
    const region = parseInt(slot.dataset.region);
    const position = parseInt(slot.dataset.position);
    
    if (isNaN(region) || isNaN(position)) return null;
    
    const visualPos = helpers.regionPositionToRankFile(region, position, layout, isFolder);
    
    return {
        rank: visualPos.rank,
        file: visualPos.file,
        screen: visualPos.screen
    };
}
